import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { wikiApi } from '../../api/wiki.js';

const REACTION_EMOJIS = ['👍', '✅', '🔥', '❤️', '👀', '🚀'];

/**
 * /wiki/:spaceKey/:pageSlug — read-only page view with comments + reactions.
 */
export default function PageView() {
  const { spaceKey, pageSlug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [backlinks, setBacklinks] = useState([]);
  const [error, setError] = useState(null);
  const [showVersions, setShowVersions] = useState(false);

  function reload() {
    setError(null);
    wikiApi.getPageBySlug(spaceKey, pageSlug)
      .then(setData)
      .catch((err) => setError(err.response?.data?.message || 'Page not found'));
  }
  useEffect(reload, [spaceKey, pageSlug]);

  useEffect(() => {
    if (!data?.page?.id) return;
    wikiApi.listBacklinks(data.page.id).then((d) => setBacklinks(d?.items || []));
  }, [data?.page?.id]);

  if (error) return <div className="alert error" style={{ margin: 24 }} role="alert">{error}</div>;
  if (!data) return <div className="muted" style={{ padding: 24 }}>Loading page…</div>;

  const { page, editor, comments, reactions } = data;

  return (
    <article data-testid="wiki-page-view" data-loaded="true" data-page-id={page.id} data-page-slug={page.slug}
             style={{ padding: '24px 32px', maxWidth: 920, margin: '0 auto' }}>
      <header>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
          <Link to={`/wiki/${spaceKey}`} style={{ color: 'inherit' }}>{spaceKey}</Link>
          {' / '}<span>{page.title}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          {page.emoji_icon && <div style={{ fontSize: 48, lineHeight: 1 }}>{page.emoji_icon}</div>}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ margin: 0, fontSize: 32 }} data-testid="wiki-page-title">{page.title}</h1>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: '#64748b', marginTop: 6 }}>
              {editor && <>Edited by <strong style={{ color: '#0f172a' }}>{editor.name}</strong> · {timeAgo(page.updated_at)}</>}
              <span className="chip" style={{ fontSize: 10 }}>v{page.version}</span>
              <span>· {page.reading_time_mins} min read · {page.word_count} words</span>
              <span className={'chip status-' + page.status} style={{ fontSize: 10 }}>{page.status}</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <Link className="btn primary" to={`/wiki/${spaceKey}/${page.slug}/edit`} data-testid="edit-page-btn">Edit</Link>
            <button className="btn ghost" onClick={() => setShowVersions(true)} data-testid="version-history-btn">History</button>
            <button className="btn ghost" onClick={async () => {
              await wikiApi.duplicatePage(page.id);
              navigate(`/wiki/${spaceKey}`);
            }}>Duplicate</button>
            <button className="btn ghost" onClick={async () => {
              if (confirm('Archive this page?')) {
                await wikiApi.archivePage(page.id);
                navigate(`/wiki/${spaceKey}`);
              }
            }}>Archive</button>
          </div>
        </div>
      </header>

      {/* Reactions row */}
      <div style={{ display: 'flex', gap: 6, marginTop: 16, flexWrap: 'wrap' }} data-testid="reactions-row">
        {reactions.map((r) => (
          <button
            key={r.emoji}
            className="btn ghost small"
            data-testid="reaction-chip"
            data-emoji={r.emoji}
            onClick={async () => {
              await wikiApi.addReaction(page.id, r.emoji);
              reload();
            }}
            title={r.users.map((u) => u.name).join(', ')}
            style={{ background: '#f1f5f9' }}
          >
            {r.emoji} {r.count}
          </button>
        ))}
        <details style={{ position: 'relative' }}>
          <summary className="btn ghost small" style={{ listStyle: 'none' }}>+ Add reaction</summary>
          <div style={{ position: 'absolute', top: '100%', left: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, padding: 6, display: 'flex', gap: 4, zIndex: 10 }}>
            {REACTION_EMOJIS.map((e) => (
              <button key={e} type="button" className="btn ghost small" onClick={async () => {
                await wikiApi.addReaction(page.id, e);
                reload();
              }}>{e}</button>
            ))}
          </div>
        </details>
      </div>

      {/* Body */}
      <div className="wiki-content" style={{ marginTop: 24, lineHeight: 1.7, fontSize: 16 }}>
        <RenderTipTap content={page.content} />
      </div>

      {/* Backlinks */}
      {backlinks.length > 0 && (
        <section style={{ marginTop: 32, padding: 12, background: '#f8fafc', borderRadius: 6 }} data-testid="backlinks-panel">
          <h3 style={{ marginTop: 0, fontSize: 14, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Referenced by ({backlinks.length})
          </h3>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {backlinks.map((b) => (
              <li key={b.id}>
                <Link to={`/wiki/${b.space_key}/${b.slug}`}>
                  {b.emoji_icon} {b.title} <span className="muted small">({b.space_key})</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Comments */}
      <CommentsPanel pageId={page.id} comments={comments} reload={reload} />

      {showVersions && (
        <VersionHistoryModal pageId={page.id} onClose={() => setShowVersions(false)} onRestored={() => { setShowVersions(false); reload(); }} />
      )}
    </article>
  );
}

// ─── TipTap JSON renderer (simple) ─────────────────────────────────────
function RenderTipTap({ content }) {
  let doc;
  try { doc = typeof content === 'string' ? JSON.parse(content || '{}') : content; }
  catch { return <pre>{String(content)}</pre>; }
  if (!doc?.content) return <p className="muted">No content yet.</p>;
  return <>{doc.content.map((node, i) => <RenderNode key={i} node={node} />)}</>;
}

function RenderNode({ node }) {
  if (!node) return null;
  if (node.type === 'paragraph') {
    return <p>{(node.content || []).map((c, i) => <RenderInline key={i} node={c} />)}</p>;
  }
  if (node.type === 'heading') {
    const Tag = `h${node.attrs?.level || 2}`;
    return <Tag>{(node.content || []).map((c, i) => <RenderInline key={i} node={c} />)}</Tag>;
  }
  if (node.type === 'bulletList') {
    return <ul>{(node.content || []).map((li, i) => (
      <li key={i}>{(li.content || []).map((p, j) => <RenderNode key={j} node={p} />)}</li>
    ))}</ul>;
  }
  if (node.type === 'orderedList') {
    return <ol>{(node.content || []).map((li, i) => (
      <li key={i}>{(li.content || []).map((p, j) => <RenderNode key={j} node={p} />)}</li>
    ))}</ol>;
  }
  if (node.type === 'blockquote') {
    return <blockquote>{(node.content || []).map((c, i) => <RenderNode key={i} node={c} />)}</blockquote>;
  }
  if (node.type === 'codeBlock') {
    const text = (node.content || []).map((c) => c.text || '').join('');
    return <pre><code>{text}</code></pre>;
  }
  if (node.type === 'horizontalRule') return <hr />;
  return null;
}

function RenderInline({ node }) {
  if (!node) return null;
  if (node.type === 'text') {
    let el = node.text || '';
    // Render [[Page Title]] as a chip-link.
    el = renderCrossLinks(el);
    if (node.marks?.length) {
      for (const mark of node.marks) {
        if (mark.type === 'bold') el = <strong>{el}</strong>;
        if (mark.type === 'italic') el = <em>{el}</em>;
        if (mark.type === 'code') el = <code>{el}</code>;
      }
    }
    return <>{el}</>;
  }
  return null;
}

function renderCrossLinks(text) {
  // Replace [[Title]], /page/<slug>, /issue/<KEY-N>, /project/<KEY> with chip-links.
  const parts = [];
  const re = /(\[\[[^\]]+\]\])|(\/(?:page|issue|project)\/[A-Za-z0-9-]+)/g;
  let last = 0, m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const token = m[0];
    if (token.startsWith('[[')) {
      const t = token.slice(2, -2);
      parts.push(<span key={m.index} className="chip" style={{ background: '#dbeafe', color: '#1e40af' }}>📄 {t}</span>);
    } else if (token.startsWith('/issue/')) {
      const key = token.slice(7);
      parts.push(<a key={m.index} href={`/pm/issues/${key}`}><code style={{ background: '#fef3c7', padding: '1px 4px', borderRadius: 3 }}>{key}</code></a>);
    } else if (token.startsWith('/project/')) {
      const key = token.slice(9);
      parts.push(<a key={m.index} href={`/pm/projects/${key}/board`}><code style={{ background: '#dcfce7', padding: '1px 4px', borderRadius: 3 }}>{key}</code></a>);
    } else if (token.startsWith('/page/')) {
      parts.push(<a key={m.index} href={token}><code style={{ background: '#f3e8ff', padding: '1px 4px', borderRadius: 3 }}>{token}</code></a>);
    }
    last = m.index + token.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length === 1 ? parts[0] : <>{parts}</>;
}

// ─── Comments panel ─────────────────────────────────────────────────────
function CommentsPanel({ pageId, comments, reload }) {
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function add(e) {
    e.preventDefault();
    if (!body.trim()) return;
    setSubmitting(true);
    try { await wikiApi.createComment(pageId, { body }); setBody(''); reload(); }
    finally { setSubmitting(false); }
  }

  // Group: top-level + their replies, plus inline-anchored vs page-level.
  const inline = comments.filter((c) => c.anchor_text && !c.parent_id);
  const pageLevel = comments.filter((c) => !c.anchor_text && !c.parent_id);
  function repliesOf(parentId) { return comments.filter((c) => c.parent_id === parentId); }

  return (
    <section style={{ marginTop: 40 }} data-testid="comments-panel">
      <h3>Comments ({comments.length})</h3>
      {inline.length > 0 && (
        <details open>
          <summary style={{ fontSize: 13, color: '#64748b', cursor: 'pointer' }}>Inline comments ({inline.length})</summary>
          {inline.map((c) => <CommentBlock key={c.id} c={c} replies={repliesOf(c.id)} reload={reload} />)}
        </details>
      )}
      {pageLevel.map((c) => <CommentBlock key={c.id} c={c} replies={repliesOf(c.id)} reload={reload} />)}

      <form onSubmit={add} style={{ marginTop: 12 }}>
        <textarea
          rows={3}
          placeholder="Add a comment…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          style={{ width: '100%' }}
          data-testid="comment-input"
        />
        <button type="submit" className="btn primary small" disabled={submitting || !body.trim()} style={{ marginTop: 6 }}>
          {submitting ? 'Posting…' : 'Comment'}
        </button>
      </form>
    </section>
  );
}

function CommentBlock({ c, replies, reload }) {
  return (
    <div data-testid="wiki-comment" data-comment-id={c.id} style={{ borderLeft: c.is_resolved ? '3px solid #16a34a' : '3px solid #e2e8f0', padding: '6px 12px', margin: '8px 0', opacity: c.is_resolved ? 0.6 : 1 }}>
      <div style={{ fontSize: 12, color: '#64748b' }}>
        <strong>{c.user_name || c.user_email}</strong> · {timeAgo(c.created_at)}
        {c.anchor_text && <span style={{ marginLeft: 6, fontStyle: 'italic' }}>— on "{c.anchor_text}"</span>}
      </div>
      <p style={{ margin: '4px 0' }}>{c.body}</p>
      {!c.is_resolved && (
        <button
          type="button"
          className="btn ghost small"
          data-testid="resolve-comment-btn"
          onClick={async () => { await wikiApi.resolveComment(c.id); reload(); }}
        >
          Resolve
        </button>
      )}
      {replies.length > 0 && (
        <div style={{ paddingLeft: 16, marginTop: 8 }}>
          {replies.map((r) => <CommentBlock key={r.id} c={r} replies={[]} reload={reload} />)}
        </div>
      )}
    </div>
  );
}

// ─── Version history modal ──────────────────────────────────────────────
function VersionHistoryModal({ pageId, onClose, onRestored }) {
  const [versions, setVersions] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    wikiApi.listVersions(pageId).then((d) => {
      setVersions(d?.items || []);
      if (d?.items?.length) setSelected(d.items[0]);
    });
  }, [pageId]);

  async function restore() {
    if (!selected) return;
    if (!confirm(`Restore version ${selected.version_no}? This creates a new version with the old content.`)) return;
    await wikiApi.restoreVersion(pageId, selected.version_no);
    onRestored();
  }

  return (
    <div role="dialog" aria-modal="true" aria-label="Version history"
         style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
         onClick={onClose}
         data-testid="version-history-modal">
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ width: 'min(95vw, 900px)', maxHeight: '85vh', display: 'grid', gridTemplateColumns: '260px 1fr', overflow: 'hidden' }}>
        <aside style={{ borderRight: '1px solid #e2e8f0', overflowY: 'auto', padding: 8 }}>
          <h3 style={{ margin: '4px 8px 12px' }}>Versions</h3>
          {versions.map((v) => (
            <button
              key={v.version_no}
              type="button"
              className="btn ghost small"
              data-testid="version-row"
              data-version-no={v.version_no}
              onClick={() => setSelected(v)}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                background: selected?.version_no === v.version_no ? '#dbeafe' : 'transparent',
                marginBottom: 4,
              }}
            >
              <div style={{ fontWeight: 600 }}>v{v.version_no}</div>
              <div className="muted small">{v.edited_by_name || 'unknown'} · {timeAgo(v.created_at)}</div>
              {v.edit_summary && <div style={{ fontSize: 11, color: '#64748b' }}>{v.edit_summary}</div>}
            </button>
          ))}
        </aside>
        <div style={{ padding: 16, overflowY: 'auto' }}>
          <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h2 style={{ margin: 0 }}>{selected ? `Version ${selected.version_no}` : 'Pick a version'}</h2>
            <div>
              {selected && versions[0] && selected.version_no !== versions[0].version_no && (
                <button className="btn primary" data-testid="restore-version-btn" onClick={restore}>Restore this version</button>
              )}
              <button className="btn ghost" onClick={onClose} style={{ marginLeft: 6 }}>Close</button>
            </div>
          </header>
          {selected && (
            <div style={{ background: '#f8fafc', padding: 12, borderRadius: 4, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap' }}>
              {selected.content_text || '(empty)'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return '';
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}
